import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Lock, Loader2, ExternalLink, Eye } from "lucide-react";

/**
 * LaunchPortalSensitiveDrawer — Paul/Sandra-only surface for the raw
 * DBS submission + bank details. Backed by
 * ``GET /admin/franchisees/{id}/launch-portal/sensitive`` which 403s
 * for everyone else. Presigned R2 URLs on DBS documents expire after
 * 15 minutes.
 */
export default function LaunchPortalSensitiveDrawer({ franchisee, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/admin/franchisees/${franchisee.id}/launch-portal/sensitive`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load sensitive data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [franchisee.id]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-stone-950/70 flex items-center justify-center p-4"
      data-testid="launch-sensitive-drawer"
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-stone-200 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-800" />
            <h2 className="font-display text-base text-amber-900">Launch Portal — Sensitive Data</h2>
          </div>
          <button onClick={onClose} data-testid="launch-sensitive-close" className="text-stone-500 hover:text-stone-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-5 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-stone-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="launch-sensitive-error">{error}</div>
          )}
          {!loading && data && (
            <>
              <SensitiveDBSPanel dbs={data.dbs} presigned={data.presigned_dbs_docs || {}} franchiseeId={franchisee.id} />
              <SensitiveDDPanel dd={data.direct_debit} />
              {data.hq_email_password && (
                <div className="rounded-xl border border-stone-200 p-4">
                  <div className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-1">HQ-set mailbox password</div>
                  <div className="font-mono text-stone-900" data-testid="launch-sensitive-mailbox-pw">{data.hq_email_password}</div>
                </div>
              )}
              <div className="text-xs text-stone-500 italic">
                Only Paul and Sandra can access this drawer. Every open is logged on the server.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function SensitiveDBSPanel({ dbs, presigned, franchiseeId }) {
  const [reopening, setReopening] = useState(false);
  const [reopenErr, setReopenErr] = useState("");
  const [reopened, setReopened] = useState(false);

  if (!dbs || (!dbs.submitted_at && !dbs.reopened_at && !dbs.forename)) {
    return (
      <div className="rounded-xl border border-stone-200 p-4">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-1">DBS Application</div>
        <div className="text-sm text-stone-500 italic">Not yet submitted by the franchisee.</div>
      </div>
    );
  }

  const showAsReopened = reopened || (!dbs.submitted_at && (dbs.reopened_at || dbs.forename));

  const doReopen = async () => {
    if (!window.confirm("Reopen this DBS for the applicant? Their existing entries and uploaded documents will be preserved.")) return;
    setReopening(true); setReopenErr("");
    try {
      await api.post(`/admin/franchisees/${franchiseeId}/launch-portal/dbs/reopen`);
      setReopened(true);
    } catch (e) {
      setReopenErr(e?.response?.data?.detail || "Could not reopen.");
    } finally {
      setReopening(false);
    }
  };
  return (
    <div className="rounded-xl border border-stone-200 p-4 space-y-3" data-testid="launch-sensitive-dbs">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500">DBS Application</div>
        {showAsReopened ? (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2 py-0.5" data-testid="launch-sensitive-dbs-reopened">Reopened for edits</span>
        ) : (
          <button
            type="button"
            disabled={reopening}
            onClick={doReopen}
            data-testid="launch-sensitive-dbs-reopen"
            className="text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-[#dddd16] hover:bg-stone-800 disabled:opacity-40 rounded-md px-2 py-1"
          >
            {reopening ? "Reopening…" : "Reopen for edits"}
          </button>
        )}
      </div>
      {reopenErr && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="launch-sensitive-dbs-reopen-error">{reopenErr}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <KV label="Forename" value={dbs.forename} />
        <KV label="Middle names" value={dbs.middle_names} />
        <KV label="Surname" value={dbs.surname} />
        <KV label="Date of birth" value={dbs.date_of_birth} />
        <KV label="Gender" value={dbs.gender} />
        <KV label="Birth surname" value={dbs.birth_surname} />
        <KV label="Birth surname used until" value={dbs.birth_surname_used_until} />
        <KV label="NI number" value={dbs.ni_number} mono />
        <KV
          label="Place of birth"
          value={[dbs.place_of_birth_town, dbs.place_of_birth_county, dbs.place_of_birth_country]
            .filter(Boolean).join(", ") || "—"}
        />
        <KV label="Nationality at birth" value={dbs.nationality_at_birth} />
        <KV label="Changed nationality since birth" value={dbs.changed_nationality === "yes" ? "Yes" : dbs.changed_nationality === "no" ? "No" : "—"} />
        {dbs.changed_nationality === "yes" && (
          <KV label="Current nationality" value={dbs.current_nationality} />
        )}
        <KV
          label="Has convictions"
          value={
            dbs.has_convictions === "yes" || dbs.has_convictions === true
              ? "Yes"
              : dbs.has_convictions === "no" || dbs.has_convictions === false
                ? "No"
                : "—"
          }
        />
      </div>
      {(dbs.has_convictions === "yes" || dbs.has_convictions === true) && dbs.conviction_details && (
        <div className="text-sm" data-testid="launch-sensitive-dbs-conviction-details">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Conviction details</div>
          <div className="whitespace-pre-wrap text-stone-900">{dbs.conviction_details}</div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Other names used</div>
        {dbs.has_other_names === "yes" ? (
          Array.isArray(dbs.other_names) && dbs.other_names.length > 0 ? (
            <ul className="space-y-1 text-sm text-stone-900" data-testid="launch-sensitive-dbs-other-names">
              {dbs.other_names.map((n, i) => (
                <li key={i} className="border-l-2 border-stone-300 pl-2">
                  <span className="tabular-nums text-stone-500 text-xs">{n.from_year} → {n.to_year}</span>
                  <div><b>{n.name}</b> <span className="text-stone-500 text-xs">({n.type})</span></div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-stone-500 italic">Yes — no entries recorded.</div>
          )
        ) : (
          <div className="text-sm text-stone-900">No</div>
        )}
      </div>
      {dbs.has_convictions && dbs.conviction_details && (
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Conviction details</div>
          <div className="whitespace-pre-wrap text-stone-900">{dbs.conviction_details}</div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Address history</div>
        <ul className="space-y-1 text-sm text-stone-900" data-testid="launch-sensitive-dbs-address-history">
          {(dbs.address_history || []).map((row, i) => {
            const hasStruct = !!(row.house_name_number || row.street || row.town_city || row.county || row.postcode);
            const legacyText = row.full_address || row.address;
            return (
              <li key={i} className="border-l-2 border-stone-300 pl-2">
                <span className="tabular-nums text-stone-500 text-xs">{row.from} → {row.to}</span>
                {hasStruct ? (
                  <div>
                    {[row.house_name_number, row.street, row.town_city, row.county, row.postcode]
                      .filter(Boolean).join(", ")}
                    {legacyText && (
                      <div className="text-[10px] text-stone-500 italic mt-0.5">
                        Legacy free-text: {legacyText}
                      </div>
                    )}
                  </div>
                ) : legacyText ? (
                  <div className="whitespace-pre-wrap">
                    {legacyText}
                    <span className="text-[10px] italic text-stone-500 ml-1">(legacy free-text)</span>
                  </div>
                ) : (
                  <div className="italic text-stone-500">—</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Uploaded documents</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {["doc1", "doc2", "doc3"].map((k) => {
            const d = dbs[k];
            if (!d) return null;
            const url = presigned[k];
            return (
              <div key={k} className="rounded-lg border border-stone-200 p-3 bg-stone-50 text-sm space-y-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{k.toUpperCase()} — {d.type}</div>
                <div className="text-stone-800 truncate" title={d.filename}>{d.filename}</div>
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 font-bold text-xs" data-testid={`launch-sensitive-${k}-view`}>
                    <Eye className="w-3.5 h-3.5" /> View <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-xs text-red-700">File unavailable</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function SensitiveDDPanel({ dd }) {
  if (!dd || !dd.submitted_at) {
    return (
      <div className="rounded-xl border border-stone-200 p-4">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-1">Direct Debit</div>
        <div className="text-sm text-stone-500 italic">Not yet submitted by the franchisee.</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-stone-200 p-4 space-y-2" data-testid="launch-sensitive-dd">
      <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Direct Debit</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <KV label="Sort code" value={dd.sort_code} mono />
        <KV label="Account number" value={dd.account_number} mono />
      </div>
    </div>
  );
}


function KV({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{label}</div>
      <div className={"text-stone-900 " + (mono ? "font-mono tabular-nums" : "")}>{value || "—"}</div>
    </div>
  );
}
